// NPM Modules
import { writable } from 'svelte/store';
import collect from 'collect.js';
import dayjs from 'dayjs';
import { mergeCollectionStore, mergeModelDataInCollectionStore } from '@/Stores/StoreHelpers.js';
import config from '@/config.js';
// Models
import SportEvent from '@/Models/SportEvent';
// Stores
import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
import MarketStore from '@/Stores/Sport/MarketStore.js';
import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';
import CompetitionStore from '@/Stores/Sport/CompetitionStore.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let meta;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getSportEvents(
			filter = {},
			reset = false,
			pagination = null,
			markets = true,
			sortDescending = false,
			includeCompetition = false
		) {
			if (!config.SPORT_ENABLED) return;

			let sportEvent = new SportEvent();

			// FILTERING
			if ('sportId' in filter && parseInt(filter.sportId)) {
				sportEvent = sportEvent.where('sport_id', filter.sportId);
			}
			if ('compId' in filter && parseInt(filter.compId)) {
				sportEvent = sportEvent.where('competition_id', filter.compId);
			}
			if ('status' in filter && !!filter.status.length) {
				// TODO: don't think the API is supporting multiple status separated by comma
				sportEvent = sportEvent.where('status_id', filter.status.join(','));
			}
			if ('minBetCount' in filter) {
				sportEvent = sportEvent.where('min_bet_count', filter.minBetCount);
			}
			if ('fromDate' in filter || 'toDate' in filter) {
				if ('toDate' in filter && !('fromDate' in filter)) {
					sportEvent = sportEvent.where('start_time', { to: encodeURIComponent(filter.toDate) });
				} else if ('fromDate' in filter && !('toDate' in filter)) {
					sportEvent = sportEvent.where('start_time', {
						from: encodeURIComponent(filter.fromDate)
					});
				} else {
					sportEvent = sportEvent.where('start_time', {
						to: encodeURIComponent(filter.toDate),
						from: encodeURIComponent(filter.fromDate)
					});
				}
			}
			if ('manual' in filter) {
				if (filter.manual === true) {
					sportEvent = sportEvent.where('remote_id', '');
				}
			}

			// SORTING
			if (sortDescending) {
				sportEvent.orderByDesc('start_time');
			}

			let results;
			if (markets) {
				const withsToGet = [
					'key_market_group.markets.selections',
					'participants',
					'sportcast_competition',
					'sportcast_fixture'
				];
				if (includeCompetition) {
					withsToGet.push('competition');
					withsToGet.push('competition.sportcast_competition');
				}
				sportEvent = sportEvent.with(withsToGet);
				results = pagination ? sportEvent.paginate(pagination.size, pagination.number) : sportEvent.get();
			} else {
				results = pagination ? sportEvent.paginate(pagination.size, pagination.number) : sportEvent.get();
			}
			return results
				.then((result) => {
					let allSportEvents = collect();
					let allMarketGroups = collect();
					let allMarkets = collect();
					let allSportSelections = collect();
					let allSportEventsCompetitions = collect();
					meta = result.meta;

					// extract each Model type to its own Collection Store
					result.data.map((sportEvent) => {
						if (markets) {
							// Market Groups
							let marketGroup = sportEvent.key_market_group;
							marketGroup.sportevent_id = sportEvent.id;
							// Markets
							marketGroup.market_ids = [];
							marketGroup.markets &&
								marketGroup.markets.data.sortBy('order').map((market) => {
									// Selections
									market.selections.data.map((selection) => {
										selection.market_id = market.id;
										allSportSelections.push(selection);
									});
									delete market.selections;
									allMarkets.push(market);
									marketGroup.market_ids.push(market.id);
								});
							allMarketGroups.push(marketGroup);
							delete sportEvent.key_market_group;
						}
						if (includeCompetition) {
							let competition = sportEvent.competition;
							allSportEventsCompetitions.push(competition);
							delete sportEvent.competition;
							sportEvent.competition = {
								id: competition.id,
								name: competition.name
							};
							if (competition.sportcast_competition_id) {
								sportEvent.competition['sportcast_competition_id'] = competition.sportcast_competition_id;
							}
						}
						allSportEvents.push(sportEvent);
					});

					if (reset) {
						// Not viewing an event, safe to reset all collections
						set(allSportEvents);
						if (markets) {
							MarketGroupStore.set(allMarketGroups);
							MarketStore.set(allMarkets);
							SportSelectionStore.set(allSportSelections);
						}
						if (includeCompetition) {
							CompetitionStore.set(allSportEventsCompetitions);
						}
					} else {
						// Polling would only need to merge as they may be viewing an event
						mergeCollectionStore(this, allSportEvents);
						if (markets) {
							mergeCollectionStore(MarketGroupStore, allMarketGroups);
							mergeCollectionStore(MarketStore, allMarkets);
							mergeCollectionStore(SportSelectionStore, allSportSelections);
						}
						if (includeCompetition) {
							mergeCollectionStore(CompetitionStore, allSportEventsCompetitions);
						}
					}
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getNextToJumpSportEvents(
			fromDate = dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
			toDate = dayjs().format('YYYY-MM-DDTHH:mm:ssZ')
		) {
			if (!config.SPORT_ENABLED) return;

			const sportevent = new SportEvent();
			sportevent
				.with(['participants'])
				.where('from', encodeURIComponent(fromDate), 'start_time')
				.where('to', encodeURIComponent(toDate), 'start_time')
				.get()
				.then((result) => {
					let allSportEvents = collect();

					result.data.map((sportevent) => {
						allSportEvents.push(sportevent);
					});

					mergeCollectionStore(this, allSportEvents);
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getEventMarketGroups(eventId) {
			if (!config.SPORT_ENABLED) return;

			const sportEvent = new SportEvent();
			return sportEvent
				.with([
					'event_market_groups.markets',
					'participants',
					'sportcast_competition',
					'sportcast_fixture',
					'promotions'
				])
				.find(eventId)
				.then((sportEvent) => {
					let allSportEvents = collect();
					let allMarketGroups = collect();
					let allMarkets = collect();
					// Market Groups
					sportEvent.event_market_groups.data.map((market_group) => {
						market_group.market_ids = [];
						market_group.markets.data.sortBy('order').map((market) => {
							allMarkets.push(market);
							market_group.market_ids.push(market.id);
						});
						delete market_group.markets;
						market_group.sportevent_id = sportEvent.id;
						allMarketGroups.push(market_group);
					});
					delete sportEvent.event_market_groups;
					allSportEvents.push(sportEvent);
					mergeCollectionStore(this, allSportEvents);
					mergeCollectionStore(MarketGroupStore, allMarketGroups);
					mergeCollectionStore(MarketStore, allMarkets.unique('id'));
				})
				.catch((e) => {
					console.error(e);
				});
		},

		updateSuspended(eventId, suspended) {
			if (!config.SPORT_ENABLED) return;

			let event = _store.firstWhere('id', eventId);
			return event
				.patch({
					suspended
				})
				.then((results) => {
					return results;
				});
		},

		updateOverrideStatus(eventId, override_status_id) {
			if (!config.SPORT_ENABLED) return;

			let event = _store.firstWhere('id', eventId);
			return event
				.patch({
					override_status_id
				})
				.then((results) => {
					return results;
				});
		},

		updateAllMarkets(eventId, data) {
			if (!config.SPORT_ENABLED) return;

			let sportEvent = new SportEvent();
			return sportEvent
				.makePersistRequest({
					url: `${sportEvent.baseUrl()}/${sportEvent.resourceName()}/${eventId}/markets`,
					method: 'POST',
					data
				})
				.then((results) => {
					return results;
				});
		},

		updateOverrideStartTime(eventId, override_start_time) {
			if (!config.SPORT_ENABLED) return;

			let event = _store.firstWhere('id', eventId);
			return event
				.patch({
					override_start_time
				})
				.then((results) => {
					return results;
				});
		},

		updateNote(eventId, note) {
			if (!config.SPORT_ENABLED) return;

			let event = _store.firstWhere('id', eventId);
			return event
				.patch({
					note
				})
				.then((results) => {
					return results;
				});
		},

		applySportEventUpdates(data) {
			if (data && data.id && _store.firstWhere('id', String(data.id))) {
				mergeModelDataInCollectionStore(data, this);
			}
		},

		createEvent(data) {
			let sportEvent = new SportEvent();

			return sportEvent
				.makePersistRequest({
					url: `${sportEvent.baseUrl()}/${sportEvent.resourceName()}`,
					method: 'PUT',
					data
				})
				.then((result) => {
					return result;
				});
		},

		updatePromotion(eventId, data) {
			if (!config.SPORT_ENABLED) return;

			let sportEvent = _store.firstWhere('id', eventId);
			return sportEvent.updatePromotion(data).then((results) => {
				this.getEventMarketGroups(eventId);
				return results;
			});
		},

		removePromotion(eventId, data) {
			if (!config.SPORT_ENABLED) return;

			let sportEvent = _store.firstWhere('id', eventId);
			return sportEvent.removePromotion(data).then((results) => {
				this.getEventMarketGroups(eventId);
				return results;
			});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
